// third-party
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// project imports
import axios2 from 'utils/axios2';
import { hasPermission, getUserContext, createAuditEntry } from '../../utils/rbac';

// ----------------------------------------------------------------------

// Async thunks
export const fetchAirflowDags = createAsyncThunk('datahubWorkflow/fetchDags', async (projectId, { rejectWithValue }) => {
    try {
        const { user_token, user_permissions, user_groups, user_id } = getUserContext();

        // Check DAGs view permission
        if (!hasPermission(user_permissions, 'dags', 'view')) {
            throw new Error('Insufficient permissions: Cannot view Airflow DAGs');
        }

        const response = await axios2.get('/datahub/workflow/dags', {
            params: {
                projectId,
                user_token,
                permissions: user_permissions,
                groups: user_groups
            }
        });

        // Create audit entry
        const auditEntry = createAuditEntry(user_id, 'fetch_airflow_dags', 'workflow', projectId, true);

        return response.data;
    } catch (error) {
        // Create audit entry for failed operation
        const { user_id } = getUserContext();
        const auditEntry = createAuditEntry(user_id, 'fetch_airflow_dags', 'workflow', projectId, false, error.message);

        return rejectWithValue(error.response?.data || error.message);
    }
});

export const triggerDag = createAsyncThunk('datahubWorkflow/triggerDag', async ({ projectId, dagId, conf = {} }, { rejectWithValue }) => {
    try {
        const { user_token, user_permissions, user_groups, user_id } = getUserContext();

        // Check DAG execution trigger permission
        if (!hasPermission(user_permissions, 'dag_execution', 'trigger')) {
            throw new Error('Insufficient permissions: Cannot trigger DAG execution');
        }

        const response = await axios2.post('/datahub/workflow/dags/trigger', {
            projectId,
            dagId,
            conf,
            user_token,
            permissions: user_permissions,
            groups: user_groups,
            audit: {
                action: 'trigger_dag',
                resource_type: 'dag_execution',
                details: {
                    dag_id: dagId,
                    conf: conf
                }
            }
        });

        // Create audit entry
        const auditEntry = createAuditEntry(user_id, 'trigger_dag', 'dag_execution', dagId, true);

        return response.data;
    } catch (error) {
        // Create audit entry for failed operation
        const { user_id } = getUserContext();
        const auditEntry = createAuditEntry(user_id, 'trigger_dag', 'dag_execution', dagId, false, error.message);

        return rejectWithValue(error.response?.data || error.message);
    }
});

export const fetchDagRuns = createAsyncThunk('datahubWorkflow/fetchDagRuns', async ({ projectId, dagId }, { rejectWithValue }) => {
    try {
        const { user_token, user_permissions, user_groups, user_id } = getUserContext();

        // Check workflow monitoring permission
        if (!hasPermission(user_permissions, 'workflow_monitoring', 'view_runs')) {
            throw new Error('Insufficient permissions: Cannot view DAG runs');
        }

        const response = await axios2.get('/datahub/workflow/dag-runs', {
            params: {
                projectId,
                dagId,
                user_token,
                permissions: user_permissions,
                groups: user_groups
            }
        });

        // Create audit entry
        const auditEntry = createAuditEntry(user_id, 'fetch_dag_runs', 'workflow_monitoring', dagId, true);

        return response.data;
    } catch (error) {
        // Create audit entry for failed operation
        const { user_id } = getUserContext();
        const auditEntry = createAuditEntry(user_id, 'fetch_dag_runs', 'workflow_monitoring', dagId, false, error.message);

        return rejectWithValue(error.response?.data || error.message);
    }
});

export const createDag = createAsyncThunk('datahubWorkflow/createDag', async ({ projectId, dagDefinition }, { rejectWithValue }) => {
    try {
        const { user_token, user_permissions, user_groups, user_id } = getUserContext();

        // Check DAG create permission
        if (!hasPermission(user_permissions, 'dags', 'create')) {
            throw new Error('Insufficient permissions: Cannot create DAGs');
        }

        const response = await axios2.post('/datahub/workflow/dags/create', {
            projectId,
            dagDefinition,
            user_token,
            permissions: user_permissions,
            groups: user_groups,
            audit: {
                action: 'create_dag',
                resource_type: 'dags',
                details: {
                    dag_name: dagDefinition.dag_id,
                    schedule: dagDefinition.schedule_interval
                }
            }
        });

        // Create audit entry
        const auditEntry = createAuditEntry(user_id, 'create_dag', 'dags', response.data.id, true);

        return response.data;
    } catch (error) {
        // Create audit entry for failed operation
        const { user_id } = getUserContext();
        const auditEntry = createAuditEntry(user_id, 'create_dag', 'dags', 'new', false, error.message);

        return rejectWithValue(error.response?.data || error.message);
    }
});

export const updateDag = createAsyncThunk('datahubWorkflow/updateDag', async ({ projectId, dagId, dagDefinition }, { rejectWithValue }) => {
    try {
        const { user_token, user_permissions, user_groups, user_id } = getUserContext();

        // Check DAG update permission
        if (!hasPermission(user_permissions, 'dags', 'update')) {
            throw new Error('Insufficient permissions: Cannot update DAGs');
        }

        const response = await axios2.put(`/datahub/workflow/dags/${dagId}`, {
            projectId,
            dagDefinition,
            user_token,
            permissions: user_permissions,
            groups: user_groups,
            audit: {
                action: 'update_dag',
                resource_type: 'dags',
                details: {
                    dag_id: dagId,
                    dag_name: dagDefinition.dag_id
                }
            }
        });

        // Create audit entry
        const auditEntry = createAuditEntry(user_id, 'update_dag', 'dags', dagId, true);

        return response.data;
    } catch (error) {
        // Create audit entry for failed operation
        const { user_id } = getUserContext();
        const auditEntry = createAuditEntry(user_id, 'update_dag', 'dags', dagId, false, error.message);

        return rejectWithValue(error.response?.data || error.message);
    }
});

export const pauseDag = createAsyncThunk('datahubWorkflow/pauseDag', async ({ projectId, dagId }, { rejectWithValue }) => {
    try {
        const { user_token, user_permissions, user_groups, user_id } = getUserContext();

        // Check DAG execution pause permission
        if (!hasPermission(user_permissions, 'dag_execution', 'pause')) {
            throw new Error('Insufficient permissions: Cannot pause DAG execution');
        }

        await axios2.post(`/datahub/workflow/dags/${dagId}/pause`, {
            projectId,
            user_token,
            permissions: user_permissions,
            groups: user_groups,
            audit: {
                action: 'pause_dag',
                resource_type: 'dag_execution',
                details: {
                    dag_id: dagId
                }
            }
        });

        // Create audit entry
        const auditEntry = createAuditEntry(user_id, 'pause_dag', 'dag_execution', dagId, true);

        return dagId;
    } catch (error) {
        // Create audit entry for failed operation
        const { user_id } = getUserContext();
        const auditEntry = createAuditEntry(user_id, 'pause_dag', 'dag_execution', dagId, false, error.message);

        return rejectWithValue(error.response?.data || error.message);
    }
});

// ----------------------------------------------------------------------

const initialState = {
    dags: [],
    currentDag: null,
    dagRuns: [],
    loading: false,
    error: null,
    stats: {
        totalDags: 0,
        activeDags: 0,
        failedRuns: 0,
        successfulRuns: 0
    }
};

// ----------------------------------------------------------------------

const slice = createSlice({
    name: 'datahubWorkflow',
    initialState,
    reducers: {
        setCurrentDag(state, action) {
            state.currentDag = action.payload;
        },
        clearError(state) {
            state.error = null;
        },
        clearDagRuns(state) {
            state.dagRuns = [];
        }
    },
    extraReducers: (builder) => {
        builder
            // fetchAirflowDags
            .addCase(fetchAirflowDags.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAirflowDags.fulfilled, (state, action) => {
                state.loading = false;
                state.dags = action.payload.dags || [];
                state.stats = action.payload.stats || state.stats;
            })
            .addCase(fetchAirflowDags.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // triggerDag
            .addCase(triggerDag.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(triggerDag.fulfilled, (state, action) => {
                state.loading = false;
                // Add new DAG run to the list
                state.dagRuns.unshift(action.payload);
            })
            .addCase(triggerDag.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // fetchDagRuns
            .addCase(fetchDagRuns.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchDagRuns.fulfilled, (state, action) => {
                state.loading = false;
                state.dagRuns = action.payload.dagRuns || [];
            })
            .addCase(fetchDagRuns.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // createDag
            .addCase(createDag.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createDag.fulfilled, (state, action) => {
                state.loading = false;
                state.dags.push(action.payload);
            })
            .addCase(createDag.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // updateDag
            .addCase(updateDag.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateDag.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.dags.findIndex((dag) => dag.id === action.payload.id);
                if (index !== -1) {
                    state.dags[index] = action.payload;
                }
            })
            .addCase(updateDag.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // pauseDag
            .addCase(pauseDag.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(pauseDag.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.dags.findIndex((dag) => dag.id === action.payload);
                if (index !== -1) {
                    state.dags[index].isPaused = true;
                }
            })
            .addCase(pauseDag.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

// ----------------------------------------------------------------------

// Reducer
export default slice.reducer;

// Actions
export const { setCurrentDag, clearError, clearDagRuns } = slice.actions;
